import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { type McpToolResult, useMcpBridge } from '../mcp/McpBridgeProvider';

/**
 * Background-task tracker (the client half of the MCP ext-tasks pattern).
 *
 * A long-running WebMCP tool (download / launch) returns an MCP task
 * `{ taskId, status: 'working' }` and does NOT block. Guardian owns that taskId
 * from then on: it polls the shell-ui host `getTaskStatus` every 5 s (via the
 * bridge's side-band `callTool`, so a poll never enters the chat loop), caps at
 * 3 h, and on a terminal outcome (or the cap) hands off to `onSettled` — which
 * posts a NEW chat turn so the LLM writes the answer. The poll loop lives here in
 * JS, because the backend LLM only acts per chat turn and never self-polls.
 */

type TaskStatus = 'working' | 'input_required' | 'completed' | 'failed' | 'cancelled';
type Task = {
  taskId: string;
  status: TaskStatus;
  statusMessage?: string;
  createdAt: string;
  lastUpdatedAt: string;
  ttlMs: number | null;
  pollIntervalMs?: number;
  result?: unknown;
  error?: unknown;
};

type UiStatus = 'running' | 'succeeded' | 'failed';
// `statusMessage` is the tool's live progress line (e.g. "Downloading … — 42%"), refreshed on
// every poll so <BackgroundTasksBar> can show the percentage climb in real time.
export type UiTask = { taskId: string; description: string; status: UiStatus; statusMessage?: string };

export type Settled = {
  taskId: string;
  description: string;
  outcome: 'completed' | 'failed' | 'timeout';
  durationMs: number;
  statusMessage?: string;
  result?: unknown; // from CompletedTask.result
  error?: unknown; // from FailedTask.error
};

// Ordered list of active tasks, consumed by <BackgroundTasksBar>.
export const BackgroundTasksContext = createContext<UiTask[]>([]);

const POLL_MS = 5_000;
const MAX_TRACK_MS = 3 * 60 * 60_000; // 3 hours
const AUTO_DISMISS_MS = 8_000;

// `C` is an opaque per-task context snapshot captured at registration (e.g. the
// originating conversation + request params) and handed back verbatim on settle, so
// the caller acts on the state as it was when the task STARTED, not the latest render.
export function useBackgroundTasks<C>(onSettled: (s: Settled, context: C) => void) {
  const { callTool } = useMcpBridge();
  const [tasks, setTasks] = useState<UiTask[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setInterval>>());

  // A poll interval can outlive the render that started it (e.g. a conversation
  // switch changes `onSettled`'s identity). Keep the latest callback in a ref so
  // the interval always hands off via the CURRENT onSettled — otherwise it would
  // post the completion note into the wrong conversation.
  const onSettledRef = useRef(onSettled);
  useEffect(() => {
    onSettledRef.current = onSettled;
  }, [onSettled]);

  const stop = useCallback((taskId: string) => {
    const t = timers.current.get(taskId);
    if (t) clearInterval(t);
    timers.current.delete(taskId);
  }, []);

  // Guardian OWNS the taskId. Called with the id read off the tool result (see
  // ChatProvider). Polls the host getTaskStatus by that id @5 s, caps at 3 h, and
  // hands the outcome to onSettled exactly once.
  const register = useCallback(
    (taskId: string, description: string, context: C) => {
      if (timers.current.has(taskId)) return; // already tracking this id
      const startedAt = Date.now();
      setTasks((prev) =>
        prev.some((t) => t.taskId === taskId) ? prev : [...prev, { taskId, description, status: 'running' }],
      );

      const settle = (outcome: Settled['outcome'], task?: Task) => {
        // Single-flight: a `callTool` poll slower than the 5 s interval lets two `tick`s
        // overlap past their `await` and both reach a terminal status. `stop` deletes the
        // timer, so a second settle finds none here and bails — no duplicate handoff turn.
        if (!timers.current.has(taskId)) return;
        stop(taskId);
        setTasks((prev) =>
          prev.map((t) =>
            t.taskId === taskId ? { ...t, status: outcome === 'completed' ? 'succeeded' : 'failed' } : t,
          ),
        );
        onSettledRef.current(
          {
            taskId,
            description,
            outcome,
            durationMs: Date.now() - startedAt,
            statusMessage: task?.statusMessage,
            result: task?.result,
            error: task?.error,
          },
          context,
        );
        setTimeout(() => setTasks((prev) => prev.filter((t) => t.taskId !== taskId)), AUTO_DISMISS_MS);
      };

      const tick = async () => {
        if (Date.now() - startedAt > MAX_TRACK_MS) return settle('timeout'); // 3 h cap → failure
        let task: Task | undefined;
        try {
          const res: McpToolResult = await callTool({ name: 'getTaskStatus', arguments: { taskId } });
          task = res?.structuredContent as Task | undefined;
        } catch {
          return; // transient — keep polling
        }
        if (!task || task.status === 'working' || task.status === 'input_required') {
          // Still running — push the latest progress line into the bar so the % updates live.
          if (task?.statusMessage) {
            const message = task.statusMessage;
            setTasks((prev) => prev.map((t) => (t.taskId === taskId ? { ...t, statusMessage: message } : t)));
          }
          return; // keep polling
        }
        settle(task.status === 'completed' ? 'completed' : 'failed', task); // completed | failed | cancelled
      };

      timers.current.set(taskId, setInterval(tick, POLL_MS));
      tick(); // poll immediately, then every 5 s
    },
    [callTool, stop], // onSettled is read via onSettledRef, so it must NOT be a dep
  );

  // Unmount cleanup — clear every interval.
  useEffect(() => {
    const active = timers.current;
    return () => {
      active.forEach((t) => {
        clearInterval(t);
      });
      active.clear();
    };
  }, []);

  return { tasks, register };
}
